/**
 * A mesa no celular (docs/spec/mobile.md): uma tela por vez, com a barra do topo (voltar, nome da
 * mesa, voz e menu) e as abas embaixo: Chat, Cena, Fichas, Pessoas e Mais.
 *
 * É outra forma de mostrar o layout da mesa (`RoomDesktop` continua dono dele, do áudio e da voz):
 * abrir uma ficha, uma cena ou uma conversa põe a aba no layout, e aqui ela aparece na aba certa,
 * à vista quando foi pedida (uma conversa privada que chega não rouba a tela: vira o selo do Chat).
 * Cada aba de baixo fica montada ao trocar de aba: o chat não perde a rolagem, nem a cena o zoom.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';
import type { LibraryItem } from '../../shared.js';
import { useLoggedNav } from '../../states/nav.js';
import { useApp } from '../../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../../ui/ContextMenu.js';
import { Icon, type IconName } from '../../ui/icons.js';
import type { VoiceChat } from '../../voice/useVoiceChat.js';
import { closeSheet, itemModule, listSheets, type DesktopLayout, type ModuleClassId, type Sheet } from '../layout.js';
import { MODULES, SheetTitle, tableName } from '../modules/registry.js';
import { ConferencePanel } from '../voice/ConferencePanel.js';
import { activeOf, chipsOf, locate, MOBILE_TABS, unreadSince, type MobileTab } from './model.js';
import { useConfirmLeave } from '../leave.js';
import { useFriendsUi } from '../../social/friendsUi.js';
import { unreadTotal } from '../../social/friendsModel.js';
import { openDm, useCanDm } from '../../social/dm.js';
import { ClockLine } from '../clock/ClockLine.js';
import { useRoomClock, useSessionRules } from '../clock/ClockPill.js';
import { pillView } from '../clock/clockModel.js';
import './mobile.css';

type Update = (fn: (l: DesktopLayout) => DesktopLayout) => void;

/** O que está aberto em "Mais": um módulo (biblioteca, música…) ou uma tabela da sala. */
type MoreModule = 'system/roomLibrary' | 'system/roomFiles' | 'system/gameSessions' | 'system/musicPlayer' | 'system/sfxPlayer';
type MoreView = { kind: 'module'; classId: MoreModule | 'system/roomSettings' } | { kind: 'table'; itemId: string };

const TAB_ICON: Record<MobileTab, IconName> = { chat: 'chat', scene: 'map', sheets: 'sheet', people: 'rooms', more: 'menu' };

/** `onInvite`: "Convidar amigos" (só com um servidor que entrega convites) */
export function MobileRoom({ layout, update, voice, canAdminister, onNewGroup, onInvite }: { layout: DesktopLayout; update: Update; voice: VoiceChat; canAdminister: boolean; onNewGroup: () => void; onInvite?: () => void }) {
  const t = useT();
  const go = useLoggedNav((s) => s.go);
  const room = useApp((s) => s.room);
  const status = useApp((s) => s.status);
  const me = useApp((s) => s.user);
  const users = useApp((s) => s.users);
  const library = useApp((s) => s.library);
  const plugins = useApp((s) => s.plugins);
  const messages = useApp((s) => s.messages);
  const myMain = useApp((s) => (s.user ? (s.members[s.user.id]?.mainCharacterId ?? null) : null));
  const requestOpenSheet = useApp((s) => s.requestOpenSheet);
  const openPrivate = useApp((s) => s.openPrivate);
  // o menu da mesa é o mesmo do computador (docs/spec/room-menus.md): moderar fica no chat, e sair pergunta antes
  const confirmLeave = useConfirmLeave();
  const settingsLabel = t(canAdminister ? 'room.settings' : 'room.settings.plugins');
  // começar a sessão de jogo fica no menu da mesa; o relógio aberto é a linha do título (docs/spec/session-clock.md).
  // Sem o registro das sessões, nada disso, nem a página delas no Mais
  const record = useSessionRules().record;
  const clockView = pillView(useRoomClock(), canAdminister, useApp((s) => s.serverFeatures.gameSessions) && record);
  const controlGameSession = useApp((s) => s.controlGameSession);
  const nextSession = clockView.kind === 'start' ? clockView.number : clockView.kind === 'ended' ? clockView.next : null;

  const [tab, setTab] = useState<MobileTab>('chat');
  const [chosen, setChosen] = useState<Partial<Record<MobileTab, string>>>({});
  const [more, setMore] = useState<MoreView | null>(null);
  const [menu, setMenu] = useState<{ kind: 'room' | 'chat' | 'scene' | 'sheet'; anchor: MenuAnchor } | null>(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  // amigos e chat: o painel em tela cheia, por cima da mesa (social/FriendsDock.tsx)
  const friendsUnread = useApp((s) => unreadTotal(s.contacts));
  const showFriends = useApp((s) => s.settings.showFriendsChat);
  const openFriends = useFriendsUi((s) => s.setOpen);
  // "Falar com o mestre": a conversa direta com o dono da mesa, no painel em tela cheia
  const canDm = useCanDm();

  // os painéis não aparecem no celular: as abas se separam pelo módulo, nas abas de baixo
  const sheets = listSheets(layout);
  const sheetKey = sheets.map((s) => `${s.id}:${s.classId}:${s.param ?? ''}`).join(',');
  const show = (to: MobileTab, sheetId?: string) => {
    setTab(to);
    if (sheetId) setChosen((c) => ({ ...c, [to]: sheetId }));
  };

  // o que foi pedido (ficha ou cena da biblioteca, conversa aberta por alguém daqui) vem para a frente
  // quando a aba dele estiver no layout: o RoomDesktop põe a aba depois deste efeito
  const pending = useRef<{ classId: ModuleClassId; param: string } | null>(null);
  const openSheet = useApp((s) => s.openSheet);
  const openChannel = useApp((s) => s.openChannel);
  useEffect(() => {
    if (!openSheet || openSheet.window) return;
    pending.current = { classId: itemModule(useApp.getState().library.find((i) => i.id === openSheet.itemId)?.kind), param: openSheet.itemId };
  }, [openSheet?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!openChannel?.focus) return;
    const c = openChannel.channel;
    pending.current = c.kind === 'pvt' ? { classId: 'system/pvtChat', param: c.to } : { classId: 'system/groupPVTChat', param: c.groupId };
  }, [openChannel?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const want = pending.current;
    const at = want ? locate(sheets, want.classId, want.param) : null;
    if (!at) return;
    pending.current = null;
    show(at.tab, at.sheetId);
  }, [sheetKey, openSheet?.nonce, openChannel?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // uma aba pedida de fora dos menus (o relógio abre as Sessões): aqui, a página dela no Mais
  useEffect(
    () =>
      useApp.subscribe((state, before) => {
        const wanted = state.openModule;
        if (!wanted || wanted === before.openModule || wanted.classId !== 'system/gameSessions') return;
        setTab('more');
        setMore({ kind: 'module', classId: wanted.classId });
      }),
    [],
  );

  // levar a uma sessão (o toque no relógio, o Ver de um aviso; docs/spec/session-clock.md,
  // "Avaliações"): a página das Sessões, também quando o pedido veio de fora da mesa, antes de ela abrir
  const sessionFocus = useApp((s) => s.sessionFocus);
  const settleFocus = useApp((s) => s.settleSessionFocus);
  const roomId = useApp((s) => s.activeRoomId);
  const [focusOpened, setFocusOpened] = useState(0);
  if (sessionFocus && !sessionFocus.opened && sessionFocus.roomId === roomId && sessionFocus.nonce !== focusOpened) {
    setFocusOpened(sessionFocus.nonce);
    setTab('more');
    setMore({ kind: 'module', classId: 'system/gameSessions' });
  }
  useEffect(() => {
    if (focusOpened) settleFocus(focusOpened, 'opened');
  }, [focusOpened, settleFocus]);
  // a mesa desligou o registro com a página das Sessões aberta: ela volta ao Mais
  if (!record && more?.kind === 'module' && more.classId === 'system/gameSessions') setMore(null);

  // Fichas sem nenhuma aberta: o meu personagem principal abre sozinho (uma vez por mesa)
  const autoOpened = useRef(false);
  useEffect(() => {
    if (tab !== 'sheets' || autoOpened.current || !myMain || chipsOf(sheets, 'sheets').length) return;
    autoOpened.current = true;
    requestOpenSheet(myMain);
  }, [tab, myMain, sheetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // não lidas do chat enquanto outra aba está à vista
  const [seenAt, setSeenAt] = useState(() => Date.now());
  // pela última mensagem: no teto da store o tamanho da lista para de mudar
  const lastMessageId = messages.at(-1)?.id;
  useEffect(() => {
    if (tab === 'chat') setSeenAt(Date.now());
  }, [tab, lastMessageId]);
  const unread = tab === 'chat' ? 0 : unreadSince(messages, me?.id, seenAt);

  if (!room) return null;
  const scenes = library.filter((i) => i.kind === 'scene').sort(byName);
  // minhas primeiro (a principal antes), depois as outras
  const sheetItems = library
    .filter((i) => i.kind === 'sheet')
    .sort((a, b) => rank(a) - rank(b) || byName(a, b));
  function rank(i: LibraryItem): number {
    return i.id === myMain ? 0 : i.ownerId === me?.id ? 1 : 2;
  }
  const tables = library.filter((i) => i.kind === 'dock' && !i.inactive);

  const chipRow = (to: MobileTab, chips: Sheet[], active: Sheet | undefined, addLabel: string, onAdd: ((anchor: MenuAnchor) => void) | null) => (
    <div className="rd-mroom__chips" role="tablist" aria-label={t(`room.mobile.tab.${to}`)}>
      {chips.map((s) => (
        <div key={s.id} className={`rd-mroom__chip${s.id === active?.id ? ' is-on' : ''}`}>
          <button type="button" role="tab" aria-selected={s.id === active?.id} className="rd-mroom__chipBtn" onClick={() => show(to, s.id)}>
            <SheetTitle sheet={s} />
          </button>
          {!s.main ? (
            <button type="button" className="rd-mroom__chipClose" aria-label={t('room.closeModule')} onClick={() => update((l) => closeSheet(l, s.id))}>
              <Icon name="close" size={14} />
            </button>
          ) : null}
        </div>
      ))}
      {onAdd ? (
        <button type="button" className="rd-mroom__chip rd-mroom__chip--add" aria-label={addLabel} title={addLabel} onClick={(e) => onAdd({ x: e.clientX, y: e.clientY })}>
          <Icon name="plus" size={16} />
        </button>
      ) : null}
    </div>
  );
  const render = (s: Sheet) => <div className="rd-mroom__module">{MODULES[s.classId].render(s)}</div>;

  const page = (to: MobileTab): ReactNode => {
    if (to === 'chat') {
      const chips = chipsOf(sheets, 'chat');
      const active = activeOf(chips, chosen.chat);
      return (
        <>
          {chipRow('chat', chips, active, t('room.mobile.newChat'), (anchor) => setMenu({ kind: 'chat', anchor }))}
          {active ? render(active) : null}
        </>
      );
    }
    if (to === 'scene' || to === 'sheets') {
      const chips = chipsOf(sheets, to);
      const active = activeOf(chips, chosen[to]);
      const items = to === 'scene' ? scenes : sheetItems;
      if (!active)
        return (
          <Picker
            title={t(to === 'scene' ? 'room.mobile.scenes.pick' : 'room.mobile.sheets.pick')}
            empty={t(to === 'scene' ? 'room.mobile.scenes.empty' : 'room.mobile.sheets.empty')}
            items={items}
            icon={to === 'scene' ? 'map' : 'sheet'}
            mark={(i) => (i.id === myMain ? t('library.main') : i.ownerId === me?.id ? t('library.mine') : null)}
            onPick={(i) => requestOpenSheet(i.id)}
          />
        );
      return (
        <>
          {chipRow(to, chips, active, t('room.mobile.openOther'), items.length ? (anchor) => setMenu({ kind: to === 'scene' ? 'scene' : 'sheet', anchor }) : null)}
          {render(active)}
        </>
      );
    }
    if (to === 'people') return render(chipsOf(sheets, 'people')[0] ?? { id: 'mobile-users', classId: 'system/stdVertUserList' });
    // Mais: a lista, ou o que foi aberto dela (com o voltar)
    if (more) {
      const sheet: Sheet =
        more.kind === 'table'
          ? (sheets.find((s) => s.classId === 'system/tableDock' && s.param === more.itemId) ?? { id: `mobile-table-${more.itemId}`, classId: 'system/tableDock', param: more.itemId })
          : (sheets.find((s) => s.classId === more.classId) ?? { id: `mobile-${more.classId}`, classId: more.classId });
      return (
        <>
          <div className="rd-mroom__subbar">
            <button type="button" className="rd-mroom__subBack" onClick={() => setMore(null)}>
              <Icon name="chevronLeft" size={20} /> {t('room.mobile.tab.more')}
            </button>
            <span className="rd-mroom__subTitle">
              <SheetTitle sheet={sheet} />
            </span>
          </div>
          {render(sheet)}
        </>
      );
    }
    const row = (icon: IconName, label: string, onClick: () => void, danger = false) => (
      <li key={label}>
        <button type="button" className={`rd-mroom__row${danger ? ' is-danger' : ''}`} onClick={onClick}>
          <Icon name={icon} size={22} />
          <span className="rd-mroom__rowLabel">{label}</span>
          {danger ? null : <span className="rd-mroom__rowChevron" aria-hidden="true">›</span>}
        </button>
      </li>
    );
    const moduleRow = (classId: MoreModule) =>
      row(MODULES[classId].icon!, t(MODULES[classId].titleKey), () => setMore({ kind: 'module', classId }));
    return (
      <ul className="rd-mroom__list" aria-label={t('room.mobile.tab.more')}>
        {moduleRow('system/roomLibrary')}
        {moduleRow('system/roomFiles')}
        {record ? moduleRow('system/gameSessions') : null}
        {tables.map((item) => row(MODULES['system/tableDock'].icon!, tableName(item, t, plugins), () => setMore({ kind: 'table', itemId: item.id })))}
        {moduleRow('system/musicPlayer')}
        {moduleRow('system/sfxPlayer')}
        {row('mic', voice.inCall ? t('room.voice.inCall') : t('room.voice'), () => setVoiceOpen(true))}
        {onInvite ? row('userPlus', t('room.inviteFriends'), onInvite) : null}
        {row('settings', settingsLabel, () => setMore({ kind: 'module', classId: 'system/roomSettings' }))}
        {row('logout', t('room.leave'), () => void confirmLeave(), true)}
      </ul>
    );
  };

  const menuEntries = (): MenuEntry[] => {
    if (!menu) return [];
    if (menu.kind === 'room')
      return [
        { kind: 'title', label: room.name },
        ...(onInvite ? [{ label: t('room.inviteFriends.menu'), icon: <Icon name="userPlus" size={18} />, onSelect: onInvite }] : []),
        ...(canDm && me && room.ownerId !== me.id
          ? [{ label: t('room.talkToGm'), icon: <Icon name="mail" size={18} />, onSelect: () => openDm(room.ownerId, users.find((u) => u.id === room.ownerId)) }]
          : []),
        {
          label: settingsLabel,
          icon: <Icon name="settings" size={18} />,
          onSelect: () => {
            show('more');
            setMore({ kind: 'module', classId: 'system/roomSettings' });
          },
        },
        ...(nextSession ? [{ label: t('clock.start', { n: nextSession }), icon: <Icon name="play" size={16} strokeWidth={0} filled />, onSelect: () => void controlGameSession('start') }] : []),
        { kind: 'separator' },
        { label: t('room.leave'), icon: <Icon name="logout" size={18} />, danger: true, onSelect: () => void confirmLeave() },
      ];
    if (menu.kind === 'chat')
      return [
        { kind: 'title', label: t('room.mobile.newChat') },
        ...users.filter((u) => u.id !== me?.id).map((u) => ({ label: t('room.mobile.chatWith', { name: u.displayName }), onSelect: () => openPrivate({ kind: 'pvt', to: u.id }, true) })),
        { label: t('module.groupPvtChat.new'), onSelect: onNewGroup },
      ];
    const items = menu.kind === 'scene' ? scenes : sheetItems;
    return [{ kind: 'title', label: t(menu.kind === 'scene' ? 'room.mobile.scenes.pick' : 'room.mobile.sheets.pick') }, ...items.map((i) => ({ label: i.name, onSelect: () => requestOpenSheet(i.id) }))];
  };

  return (
    <div className="rd-mroom">
      <header className="rd-mroom__top">
        <button type="button" className="rd-mroom__action" aria-label={t('room.mobile.back')} onClick={() => go({ kind: 'dashboard' })}>
          <Icon name="chevronLeft" size={26} />
        </button>
        <div className="rd-mroom__title">
          <span className="rd-mroom__name">{room.name}</span>
          {status !== 'online' ? (
            <span className="rd-mroom__status" role="status">
              {t('room.status.connecting')}
            </span>
          ) : (
            <ClockLine />
          )}
        </div>
        {showFriends ? (
          <button type="button" className="rd-mroom__action rd-mroom__friends" aria-label={friendsUnread ? `${t('friends.title')} (${friendsUnread})` : t('friends.title')} onClick={() => openFriends(true)}>
            <Icon name="contacts" size={22} />
            {friendsUnread ? <span className="rd-navbtn__badge">{friendsUnread > 99 ? '99+' : friendsUnread}</span> : null}
          </button>
        ) : null}
        <button type="button" className={`rd-mroom__action${voice.inCall ? ' is-on' : ''}`} aria-label={voice.inCall ? t('room.voice.inCall') : t('room.voice')} onClick={() => setVoiceOpen(true)}>
          <Icon name={voice.inCall && !voice.micOn ? 'micOff' : 'mic'} size={22} />
        </button>
        <button type="button" className="rd-mroom__action" aria-label={t('room.menu')} onClick={(e) => setMenu({ kind: 'room', anchor: { x: e.clientX, y: e.clientY } })}>
          <Icon name="menu" size={22} />
        </button>
      </header>
      <div className="rd-mroom__body">
        {MOBILE_TABS.map((to) => (
          <section key={to} className="rd-mroom__page" hidden={to !== tab} aria-label={t(`room.mobile.tab.${to}`)}>
            {page(to)}
          </section>
        ))}
      </div>
      <nav className="rd-mnav rd-mroom__nav" aria-label={t('room.mobile.tabs')}>
        {MOBILE_TABS.map((to) => {
          const label = t(`room.mobile.tab.${to}`);
          const badge = to === 'chat' ? unread : 0;
          return (
            <button
              key={to}
              type="button"
              className={`rd-mnav__btn${to === tab ? ' is-on' : ''}`}
              aria-label={badge ? `${label} (${t('logged.rooms.unread', { count: badge })})` : label}
              aria-current={to === tab ? 'page' : undefined}
              onClick={() => (to === 'more' && tab === 'more' ? setMore(null) : show(to))}
            >
              <span className="rd-mnav__icon">
                <Icon name={TAB_ICON[to]} size={24} strokeWidth={1.8} />
                {badge ? <span className="rd-navbtn__badge">{badge > 99 ? '99+' : badge}</span> : null}
              </span>
              <span className="rd-mnav__label" aria-hidden="true">
                {label}
              </span>
            </button>
          );
        })}
      </nav>
      {menu ? <ContextMenu anchor={menu.anchor} label={menu.kind === 'room' ? t('room.menu') : t('room.mobile.newChat')} entries={menuEntries()} onClose={() => setMenu(null)} /> : null}
      {voiceOpen ? (
        <BottomSheet onClose={() => setVoiceOpen(false)}>
          <ConferencePanel voice={voice} />
        </BottomSheet>
      ) : null}
    </div>
  );
}

const byName = (a: LibraryItem, b: LibraryItem) => a.name.localeCompare(b.name);

/** Lista para tocar e abrir (as cenas, as fichas), quando nada está aberto. */
function Picker({ title, empty, items, icon, mark, onPick }: { title: string; empty: string; items: LibraryItem[]; icon: IconName; mark: (item: LibraryItem) => string | null; onPick: (item: LibraryItem) => void }) {
  return (
    <div className="rd-mroom__picker">
      <h2 className="rd-mroom__pickerTitle">{title}</h2>
      {items.length ? (
        <ul className="rd-mroom__list">
          {items.map((item) => {
            const tag = mark(item);
            return (
              <li key={item.id}>
                <button type="button" className="rd-mroom__row" onClick={() => onPick(item)}>
                  <Icon name={icon} size={22} />
                  <span className="rd-mroom__rowLabel">{item.name}</span>
                  {tag ? <span className="rd-library__mine">{tag}</span> : null}
                  <span className="rd-mroom__rowChevron" aria-hidden="true">
                    ›
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rd-mroom__empty">{empty}</p>
      )}
    </div>
  );
}

/**
 * Folha presa embaixo, com um conteúdo qualquer (a voz e o vídeo, que já se apresenta como diálogo).
 * Tocar no fundo ou Esc fecha.
 */
function BottomSheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="rd-actionSheet" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rd-actionSheet__panel rd-mroom__sheet">
        {children}
      </div>
    </div>
  );
}
